import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Mail, Phone, Link2, UserPlus, Users, CheckCircle, Clock, XCircle, Copy, ArrowUpRight, ArrowDownLeft, RotateCcw, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { InviteCarrierModal } from './InviteCarrierModal';
import { useLanguage } from "@/contexts/LanguageContext";
import { formatAppDate } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useTranslation } from "react-i18next";
import { generateInviteLink } from "@/utils/linkUtils";


interface Connection {
  id: string;
  inviter_org_id: string;
  invitee_org_id: string;
  status: "active" | "inactive";
  connection_type: string;
  invited_by: string;
  created_at: string;
  updated_at: string;
  // Joined organization data (the other party in the connection)
  organization?: {
    id: string;
    name: string;
    username?: string;
  };
  // Direction info
  isOutgoing: boolean; // true if we sent the invitation, false if we received it
}

interface ConnectionInvitation {
  id: string;
  invitation_type: 'organization' | 'email' | 'phone' | 'link';
  invitation_target: string;
  status: 'active' | 'closed' | 'accepted' | 'rejected';
  created_at: string;
  expires_at: string;
  inviter_org_id: string;
  invited_by: string;
  isOutgoing: boolean;
  organization?: {
    id: string;
    name: string;
    username?: string;
  };
}

interface CarrierManagementProps {
  companyName: string;
}

export const CarrierManagement = ({ companyName }: CarrierManagementProps) => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [invitations, setInvitations] = useState<ConnectionInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<Connection | null>(null);
  const [carrierModalOpen, setCarrierModalOpen] = useState(false);
  const [carrierContacts, setCarrierContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteMethod, setInviteMethod] = useState<"email" | "phone" | "link">("email");
  const [inviteValue, setInviteValue] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<any[]>([]);
  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<"active" | "pending">("active");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<ConnectionInvitation | null>(null);
  const { toast } = useToast();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { t, i18n } = useTranslation();

  // Load connections data
  useEffect(() => {
    if (user && currentOrganization) {
      loadConnections();
    }
  }, [user, currentOrganization]);

  const loadConnections = async () => {
    if (!currentOrganization) return;
    
    try {
      setLoading(true);
      
      // 1. Load connection invitations (all types)
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('connection_invitations')
        .select('*')
        .eq('inviter_org_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });

      if (invitationsError) throw invitationsError;

      // Get organization IDs from invitations for organization type
      const invitationOrgIds = new Set<string>();
      invitationsData?.forEach(inv => {
        if (inv.invitation_type === 'organization') {
          invitationOrgIds.add(inv.invitation_target);
        }
      });

      // Fetch organization names for invitation targets
      let invitationOrgsMap = new Map();
      if (invitationOrgIds.size > 0) {
        const { data: invitationOrgsData, error: invitationOrgsError } = await supabase
          .from('organizations')
          .select('id, name, username')
          .in('id', Array.from(invitationOrgIds));
        
        if (invitationOrgsError) throw invitationOrgsError;
        
        invitationOrgsData?.forEach(org => invitationOrgsMap.set(org.id, org));
      }

      const formattedInvitations: ConnectionInvitation[] = invitationsData?.map(inv => ({
        id: inv.id,
        invitation_type: inv.invitation_type as 'organization' | 'email' | 'phone' | 'link',
        invitation_target: inv.invitation_target,
        status: inv.status as 'active' | 'closed' | 'accepted' | 'rejected',
        created_at: inv.created_at,
        expires_at: inv.expires_at,
        inviter_org_id: inv.inviter_org_id,
        invited_by: inv.invited_by,
        isOutgoing: true,
        organization: inv.invitation_type === 'organization' ? invitationOrgsMap.get(inv.invitation_target) : undefined
      })) || [];

      setInvitations(formattedInvitations);

      // 2. Get accepted connections where current org is EITHER the inviter OR invitee
      // 2a. Connections where we are the inviter (outgoing)
      const { data: outgoingData, error: outgoingError } = await supabase
        .from('connections')
        .select('*')
        .eq('inviter_org_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });

      if (outgoingError) throw outgoingError;

      // 2b. Connections where we are the invitee (incoming) 
      const { data: incomingData, error: incomingError } = await supabase
        .from('connections')
        .select('*')
        .eq('invitee_org_id', currentOrganization.organization_id)
        .order('created_at', { ascending: false });

      if (incomingError) throw incomingError;

      // 3. Get all unique organization IDs we need to fetch
      const allConnections = [...(outgoingData || []), ...(incomingData || [])];
      const orgIds = new Set<string>();
      
      allConnections.forEach(conn => {
        // For outgoing connections, we need the invitee org
        if (outgoingData?.includes(conn)) {
          orgIds.add(conn.invitee_org_id);
        }
        // For incoming connections, we need the inviter org  
        if (incomingData?.includes(conn)) {
          orgIds.add(conn.inviter_org_id);
        }
      });

      // 4. Fetch organization details
      let organizationsMap = new Map();
      if (orgIds.size > 0) {
        const { data: orgsData, error: orgsError } = await supabase
          .from('organizations')
          .select('id, name, username')
          .in('id', Array.from(orgIds));
        
        if (orgsError) throw orgsError;
        
        orgsData?.forEach(org => organizationsMap.set(org.id, org));
      }

      // 5. Combine and format the data
      const formattedConnections: Connection[] = [
        // Outgoing connections (we invited them)
        ...(outgoingData?.map(conn => ({
          ...conn,
          status: conn.status as "active" | "inactive",
          organization: organizationsMap.get(conn.invitee_org_id),
          isOutgoing: true
        })) || []),
        // Incoming connections (they invited us)
        ...(incomingData?.map(conn => ({
          ...conn,
          status: conn.status as "active" | "inactive", 
          organization: organizationsMap.get(conn.inviter_org_id),
          isOutgoing: false
        })) || [])
      ];

      // Sort by creation date (most recent first)
      formattedConnections.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      console.log('Loaded bidirectional connections:', formattedConnections);
      setConnections(formattedConnections);
    } catch (error) {
      console.error('Error loading connections:', error);
      toast({
        title: t('carrierManagement.errorLoadingCarriers'),
        description: t('carrierManagement.failedToLoadNetwork'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const detectInputType = (input: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Phone detection disabled for now
    // const phoneRegex = /^[\+]?[0-9][\d]{3,14}$/;
    
    if (emailRegex.test(input)) return 'email';
    // if (phoneRegex.test(input.replace(/[\s\-\(\)]/g, ''))) return 'phone';
    return 'search';
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^[\+]?[0-9]{10,14}$/;
    return phoneRegex.test(cleanPhone);
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return t('carrierManagement.noPhone');
    
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    // Saudi numbers with +966
    if (cleaned.startsWith('+966')) {
      const number = cleaned.substring(4);
      if (number.length >= 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    }
    
    // Saudi numbers with 0966 (convert to +966)
    if (cleaned.startsWith('0966')) {
      const number = cleaned.substring(4);
      if (number.length >= 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    }
    
    // Other international numbers
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // Local numbers - assume Saudi if starts with 5
    if (cleaned.startsWith('5') && cleaned.length >= 9) {
      return `+966 ${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5)}`;
    }
    
    return phone;
  };

  const searchCarriers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const inputType = detectInputType(searchTerm);
    
    if (inputType === 'email') {
      // For email, show option to create new invitation
      setSearchResults([]);
      return;
    }

    // For organization search
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, username, type')
        .or(`name.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`)
        .neq('type', 'customer')
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching carriers:', error);
    }
  };

  const addCarrier = (carrier: any) => {
    // Check if already connected - don't allow adding
    if (isAlreadyConnected(carrier.id)) {
      toast({
        title: t('carrierManagement.alreadyConnected'),
        description: `You already have an active connection with ${carrier.name}`,
        variant: "default"
      });
      return;
    }

    // Check if already in selected carriers
    if (!selectedCarriers.find(c => c.id === carrier.id)) {
      setSelectedCarriers([...selectedCarriers, carrier]);
    }
    setCarrierName("");
    setSearchResults([]);
  };

  // Helper function to check if there's an existing connection with a carrier
  const isAlreadyConnected = (carrierId: string): boolean => {
    return connections.some(conn => 
      (conn.inviter_org_id === carrierId || conn.invitee_org_id === carrierId) && 
      conn.status === 'active'
    );
  };

  // Helper function to check if there's a pending invitation with a carrier
  const hasPendingInvitation = (carrierId: string): boolean => {
    return invitations.some(inv => 
      (inv.invitation_type === 'organization' && inv.invitation_target === carrierId) && 
      inv.status === 'active'
    );
  };

  const removeCarrier = (carrierId: string) => {
    setSelectedCarriers(selectedCarriers.filter(c => c.id !== carrierId));
  };

  const handleInviteCarriers = async () => {
    if (selectedCarriers.length === 0) return;
    if (!user || !currentOrganization) return;
    if (sendingInvitations) return; // Prevent double-clicks

    setSendingInvitations(true);
    try {
      // Create connection invitation records for all selected carriers
      const emailInvitations: any[] = [];
      const regularInvitations: any[] = [];
      
      for (const carrier of selectedCarriers) {
        if (carrier.isNew) {
          // For email/phone contacts
          const inputType = detectInputType(carrier.contact);
          const invitation = {
            inviter_org_id: currentOrganization.organization_id,
            invited_by: user.id,
            invitation_type: inputType,
            invitation_target: carrier.contact,
            invitee_email: inputType === 'email' ? carrier.contact : null,
            invitee_phone: null, // Phone invitations disabled
            connection_type: 'carrier'
          };
          
          if (inputType === 'email') {
            emailInvitations.push(invitation);
          } else {
            regularInvitations.push(invitation);
          }
        } else {
          // For existing organizations
          regularInvitations.push({
            inviter_org_id: currentOrganization.organization_id,
            invited_by: user.id,
            invitation_type: 'organization',
            invitation_target: carrier.id,
            invitee_org_id: carrier.id,
            connection_type: 'carrier'
          });
        }
      }

      // Insert all invitations to database first
      const allInvitations = [...emailInvitations, ...regularInvitations];
      const { data: insertedInvitations, error: insertError } = await supabase
        .from('connection_invitations')
        .insert(allInvitations)
        .select();

      if (insertError) throw insertError;

      // Notifications are automatically sent via database triggers
      // Each invitation created will trigger the queue_invitation_sent_notification function

      // Show success message
      const totalInvitations = allInvitations.length;
      
      if (totalInvitations > 0) {
        toast({
          title: t('Success'),
          description: `${totalInvitations} invitation${totalInvitations !== 1 ? 's' : ''} created successfully. Notifications will be sent automatically.`,
        });
      }

      // Reset form
      setSelectedCarriers([]);
      setCarrierName("");
      setInviteValue("");
      setSearchResults([]);
      setGeneratedLinks([]);
      setIsInviteOpen(false);
      
      // Reload connections
      loadConnections();
    } catch (error) {
      console.error('Error inviting carriers:', error);
      toast({
        title: t('Error'),
        description: t('carrierManagement.failedToSendInvitations'),
        variant: "destructive"
      });
    } finally {
      setSendingInvitations(false);
    }
  };

  // Handle resending invitations
  const handleResendInvitation = async (invitation: ConnectionInvitation) => {
    console.log('🔄 handleResendInvitation called', invitation);
    if (!user || !currentOrganization) return;
    
    try {
      console.log('🔄 Setting resendLoading to true');
      setResendLoading(true);
      
      // Determine invitation context based on org types
      let invitationContext = 'general_business';
      if (currentOrganization.organization_type === 'customer') {
        invitationContext = 'customer_to_carrier';
      } else if (currentOrganization.organization_type === 'carrier') {
        invitationContext = 'carrier_to_customer';
      }
      
      // For resend, create a new notification record in the queue to stay consistent
      const { error } = await supabase
        .from('notification_queue')
        .insert({
          event_type: 'invitation_sent',
          event_data: { invitation_id: invitation.id },
          channels: ['slack', 'email'],
          recipients: {
            email: invitation.invitation_type === 'email' ? [invitation.invitation_target] : []
          },
          priority: 'normal',
          scheduled_for: new Date().toISOString()
        });
      
      if (error) {
        console.error('Resend invitation error:', error);
        toast({
          title: t('Error'),
          description: t('Failed to resend invitation'),
          variant: 'destructive',
        });
      } else {
        // Update the resent timestamp in database
        const { error: updateError } = await supabase
          .from('connection_invitations')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', invitation.id);
        
        if (updateError) {
          console.error('Update invitation error:', updateError);
        }
        
        // Show appropriate success message based on invitation type
        let successMessage = t('Invitation resent successfully');
        if (invitation.invitation_type === 'email') {
          successMessage = t('Invitation email resent successfully');
        } else if (invitation.invitation_type === 'organization') {
          successMessage = t('Invitation notifications sent to organization members');
        } else if (invitation.invitation_type === 'phone') {
          successMessage = t('Invitation sent via phone');
        }
        
        // Note: For resend, we don't copy to clipboard, just show success message
        
        toast({
          title: t('Success'),
          description: successMessage,
        });
      }
      
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast({
        title: t('Error'),
        description: t('Failed to resend invitation'),
        variant: 'destructive',
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleShareGenericLink = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!currentOrganization || !user || generatingLink) return;

    try {
      setGeneratingLink(true);
      
      // Check if there's already a pending link invitation
      const existingLinkInvitation = invitations.find(
        inv => inv.invitation_type === 'link' && 
               inv.status === 'active' && 
               inv.inviter_org_id === currentOrganization.organization_id
      );

      if (existingLinkInvitation) {
        // Reuse existing invitation
        const inviteLink = `${window.location.origin}/connect?invitation=${existingLinkInvitation.id}`;
        
        // Copy to clipboard
        await navigator.clipboard.writeText(inviteLink);
        
        // Add to generated links display if not already there
        if (!generatedLinks.includes(inviteLink)) {
          setGeneratedLinks(prev => [...prev, inviteLink]);
        }
        
        toast({
          title: "Existing invitation link found!",
          description: "Link copied to clipboard. Using your existing pending invitation.",
        });
        
        return;
      }
      
      // Create a new 'link' type invitation record in the database
      const { data: invitation, error: insertError } = await supabase
        .from('connection_invitations')
        .insert({
          inviter_org_id: currentOrganization.organization_id,
          invited_by: user.id,
          invitation_type: 'link',
          invitation_target: 'generic_link',
          connection_type: 'carrier'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Generate the same invitation link format as other invitations
      const inviteLink = `${window.location.origin}/connect?invitation=${invitation.id}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(inviteLink);
      
      // Add to generated links display
      setGeneratedLinks(prev => [...prev, inviteLink]);
      
      // Add to local invitations state instead of reloading everything
      const newInvitation: ConnectionInvitation = {
        id: invitation.id,
        invitation_type: 'link',
        invitation_target: 'generic_link',
        status: 'active',
        created_at: invitation.created_at,
        expires_at: invitation.expires_at,
        inviter_org_id: invitation.inviter_org_id,
        invited_by: invitation.invited_by,
        isOutgoing: true
      };
      
      setInvitations(prev => [newInvitation, ...prev]);
      
      toast({
        title: "Invitation link generated!",
        description: "Link copied to clipboard and displayed below.",
      });

    } catch (error) {
      console.error('Error creating generic link:', error);
      toast({
        title: "Error creating link",
        description: "Failed to create invitation link",
        variant: "destructive"
      });
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCancelInvitation = async (invitation: ConnectionInvitation) => {
    try {
      const { error } = await supabase
        .from('connection_invitations')
        .update({ status: 'closed' })
        .eq('id', invitation.id);

      if (error) throw error;

      // Remove from local state
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      
      // Remove from generated links if it's there
      const inviteLink = `${window.location.origin}/connect?invitation=${invitation.id}`;
      setGeneratedLinks(prev => prev.filter(link => link !== inviteLink));

      toast({
        title: "Invitation cancelled",
        description: "The invitation link has been cancelled.",
      });
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast({
        title: "Error",
        description: "Failed to cancel invitation",
        variant: "destructive"
      });
    }
  };

  const handleConnectionAction = async (connectionId: string, action: 'accept' | 'decline') => {
    try {
      const { error } = await supabase
        .from('connections')
        .update({ 
          status: action === 'accept' ? 'active' : 'inactive',
          updated_at: new Date().toISOString()
        })
        .eq('id', connectionId);

      if (error) throw error;

      toast({
        title: action === 'accept' ? "Connection accepted!" : "Connection declined",
        description: `You have ${action}ed the carrier invitation.`,
      });

      // Reload connections
      loadConnections();
    } catch (error) {
      console.error('Error updating connection:', error);
      toast({
        title: "Error updating connection",
        description: "Failed to update the connection status",
        variant: "destructive"
      });
    }
  };

  const generateOrderLink = (connection: Connection) => {
    // Use the correct organization ID based on direction
    const targetOrgId = connection.isOutgoing ? connection.invitee_org_id : connection.inviter_org_id;
    const orderLink = `${window.location.origin}/order-accept/demo-order-123?carrier=${targetOrgId}`;
    navigator.clipboard.writeText(orderLink);
    toast({
      title: "Order link copied!",
      description: "Share this link for this specific load.",
    });
  };

  const getStatusColor = (status: Connection["status"]) => {
    switch (status) {
      case "active": return "success";
      case "inactive": return "destructive";
      default: return "secondary";
    }
  };

  const getStatusIcon = (status: Connection["status"]) => {
    switch (status) {
      case "active": return <CheckCircle className="h-4 w-4" />;
      case "inactive": return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusLabel = (status: Connection["status"]) => {
    switch (status) {
      case "active": return t('carrierManagement.statusActive');
      case "inactive": return t('carrierManagement.statusInactive');
      default: return t('carrierManagement.unknown');
    }
  };

  const handleCarrierClick = async (connection: Connection) => {
    console.log('Carrier clicked:', connection);
    console.log('Organization ID:', connection.organization?.id);
    setSelectedCarrier(connection);
    setCarrierModalOpen(true);
    
    // Fetch contact information for the organization
    await fetchCarrierContacts(connection.organization?.id);
  };

  const fetchCarrierContacts = async (orgId?: string) => {
    if (!orgId) {
      console.log('❌ No orgId provided to fetchCarrierContacts');
      setCarrierContacts([]);
      return;
    }

    console.log('🔍 Fetching contacts for org:', orgId);
    setContactsLoading(true);

    try {
      // Try a different approach - get members first, then get their profiles separately
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select('user_id, role')
        .eq('org_id', orgId);

      console.log('📊 Members query result:', { members, error: membersError });

      if (membersError) throw membersError;

      if (!members || members.length === 0) {
        console.log('⚠️ No members found for organization:', orgId);
        setCarrierContacts([]);
        return;
      }

      // Now get profiles for each member
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, email, phone, full_name')
        .in('user_id', userIds);

      console.log('📊 Profiles query result:', { profiles, error: profilesError });

      if (profilesError) throw profilesError;

      // Match profiles with members
      const contacts = members?.map((member, index) => {
        console.log(`👤 Processing member ${index}:`, member);
        const profile = profiles?.find(p => p.user_id === member.user_id);
        console.log(`👤 Found profile for member:`, profile);
        
        return {
          name: profile?.full_name || t('carrierManagement.unknown'),
          email: profile?.email,
          phone: profile?.phone,
          role: member.role
        };
      }) || [];

      console.log('👥 Formatted contacts:', contacts);
      setCarrierContacts(contacts);
    } catch (error) {
      console.error('❌ Error fetching carrier contacts:', error);
      setCarrierContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const activeCarriers = connections.filter(c => c.status === "active");
  const inactiveConnections = connections.filter(c => c.status === "inactive");
  const activeInvitations = invitations.filter(i => i.status === "active");
  const acceptedInvitations = invitations.filter(i => i.status === "accepted");
  const rejectedInvitations = invitations.filter(i => i.status === "rejected");

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('carrierManagement.carrierNetwork')}</h2>
          <p className="text-muted-foreground">{t('carrierManagement.manageTrustedCarriers')}</p>
        </div>
        
        <Button onClick={() => setIsInviteOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          {t('carrierManagement.inviteCarrier')}
        </Button>
        
        <InviteCarrierModal
          isOpen={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          onInviteSent={loadConnections}
          connections={connections}
          invitations={invitations}
        />
      </div>

      {/* Stats Cards - Clickable Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${selectedFilter === 'active' ? 'ring-2 ring-success bg-success/5' : 'hover:bg-muted/50'}`}
          onClick={() => setSelectedFilter('active')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{activeCarriers.length}</p>
              <p className="text-sm text-muted-foreground">{t('carrierManagement.activeCarriers')}</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${selectedFilter === 'pending' ? 'ring-2 ring-warning bg-warning/5' : 'hover:bg-muted/50'}`}
          onClick={() => setSelectedFilter('pending')}
        >
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{inactiveConnections.length + activeInvitations.length}</p>
              <p className="text-sm text-muted-foreground">{t('carrierManagement.pendingInvites')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Carriers List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('carrierManagement.yourCarrierNetwork')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Display filtered invitations - ONLY show when pending filter is selected */}
            {selectedFilter === 'pending' && invitations
              .filter(invitation => invitation.status === 'active')
              .map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg bg-blue-50/50">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-foreground">
                        {invitation.invitation_type === 'organization' 
                          ? invitation.organization?.name || t('carrierManagement.unknownOrganization')
                          : invitation.invitation_type === 'email' 
                          ? invitation.invitation_target
                          : invitation.invitation_type === 'phone'
                          ? invitation.invitation_target
                          : t('carrierManagement.linkInvitation')
                        }
                      </h3>
                      <div className="flex items-center gap-1">
                        <ArrowUpRight className="h-3 w-3 text-blue-500" />
                        <Badge variant="secondary" className="text-xs">
                          {invitation.invitation_type}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Invited {formatAppDate(invitation.created_at, language)}</span>
                      <span>• Expires {formatAppDate(invitation.expires_at, language)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge 
                    variant="secondary" 
                    className={`flex items-center gap-1 ${
                      invitation.status === 'accepted' ? 'bg-green-100 text-green-800 border-green-200' :
                      invitation.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-200' :
                      'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    <Clock className="h-4 w-4" />
                    {invitation.status === 'active' ? t('carrierManagement.statusActive') : invitation.status}
                  </Badge>
                  
                  {/* Unified Resend button - hide for link invitations */}
                  {invitation.invitation_type !== 'link' && invitation.status === 'active' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResendInvitation(invitation);
                      }}
                      disabled={resendLoading}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                       {t('carrierManagement.resend')}
                    </Button>
                  )}
                    
                    {/* Copy link button for link type invitations */}
                    {invitation.invitation_type === 'link' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const inviteLink = generateInviteLink(invitation.id);
                          navigator.clipboard.writeText(inviteLink);
                          toast({
                            title: "Link copied!",
                            description: "Invitation link copied to clipboard",
                          });
                        }}
                        className="bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        {t('carrierManagement.copyLink')}
                      </Button>
                    )}
                  
                  {/* Cancel button for active invitations - always last */}
                  {invitation.status === 'active' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setInvitationToCancel(invitation);
                        setCancelDialogOpen(true);
                      }}
                      className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    >
                      <X className="h-4 w-4 mr-1" />
                       {t('carrierManagement.cancel')}
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Display filtered connections */}
            {connections
              .filter(connection => 
                selectedFilter === 'active' ? connection.status === 'active' : connection.status === 'inactive'
              )
              .map((connection) => (
              <div 
                key={connection.id} 
                className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleCarrierClick(connection)}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-foreground">
                        {connection.organization?.name || t('carrierManagement.unknownCarrier')}
                      </h3>
                      {/* Direction indicator removed as requested */}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {connection.organization?.username && (
                        <span className="flex items-center gap-1">
                          <Link2 className="h-3 w-3" />
                          @{connection.organization.username}
                        </span>
                      )}
                      <span>• {formatAppDate(connection.created_at, language)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge 
                    variant={getStatusColor(connection.status) as any} 
                    className={`flex items-center gap-1 ${connection.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : ''}`}
                  >
                    {getStatusIcon(connection.status)}
                    {getStatusLabel(connection.status)}
                  </Badge>
                  
                  {/* Show action buttons based on status and direction */}
                  {connection.status === "inactive" && !connection.isOutgoing && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConnectionAction(connection.id, 'accept')}
                        className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConnectionAction(connection.id, 'decline')}
                        className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  )}
                  
                </div>
              </div>
            ))}
          </div>

          {/* Show empty state for filtered results */}
          {selectedFilter === 'active' && activeCarriers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('carrierManagement.noActiveCarriers')}</p>
            </div>
          )}
          
          {selectedFilter === 'pending' && inactiveConnections.length === 0 && activeInvitations.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('carrierManagement.noPendingInvitations')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Carrier Details Modal */}
      <Dialog open={carrierModalOpen} onOpenChange={setCarrierModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedCarrier?.organization?.name || t('carrierManagement.carrierDetails')}
            </DialogTitle>
            <DialogDescription>
              {t('carrierManagement.viewCarrierInfo')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCarrier && (
            <div className="space-y-6">
              {/* Basic Information & Connection Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">{t('carrierManagement.connectionDetails')}</h3>
                
                {/* Basic Company Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.companyName')}</Label>
                    <p className="text-lg font-semibold text-foreground mt-1">{selectedCarrier.organization?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.username')}</Label>
                     <p className="text-lg font-semibold text-foreground mt-1">
                       {selectedCarrier.organization?.username ? <span dir="ltr">@{selectedCarrier.organization.username}</span> : t('carrierManagement.notSet')}
                     </p>
                  </div>
                </div>
                
                {/* Connection Status & Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.connectionStatus')}</Label>
                    <div className="mt-1">
                      <Badge 
                        variant={getStatusColor(selectedCarrier.status) as any}
                        className={`${selectedCarrier.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : ''}`}
                      >
                        {getStatusIcon(selectedCarrier.status)}
                        <span className="ml-1">{getStatusLabel(selectedCarrier.status)}</span>
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.connectionType')}</Label>
                    <p className="text-sm font-medium capitalize mt-1">{selectedCarrier.connection_type}</p>
                  </div>
                </div>

                {/* Connection Timeline */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.connected')}</Label>
                    <p className="text-sm font-medium mt-1">{formatAppDate(selectedCarrier.created_at, language)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('carrierManagement.direction')}</Label>
                    <p className="text-sm font-medium mt-1">{selectedCarrier.isOutgoing ? t('carrierManagement.youInvitedThem') : t('carrierManagement.theyInvitedYou')}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">{t('carrierManagement.contactInformation')}</h3>
                <div className="bg-muted/30 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {contactsLoading ? (
                    <div className="text-center py-4">
                      <div className="flex items-center gap-2 justify-center">
                        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                        <span className="text-sm text-muted-foreground">{t('carrierManagement.loadingContacts')}</span>
                      </div>
                    </div>
                  ) : carrierContacts.length > 0 ? (
                    <div className="space-y-2">
                      {carrierContacts.map((contact, index) => (
                        <div key={index} className="border border-muted/50 rounded-md p-2 bg-background/50">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{contact.name}</span>
                            <Badge variant="outline" className="text-xs px-1 py-0">
                              {contact.role}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              <span className="truncate">{contact.email || t('carrierManagement.noEmail')}</span>
                              {contact.email && contact.email !== t('carrierManagement.noEmail') && (
                                <Copy 
                                  className="h-3 w-3 text-muted-foreground cursor-pointer hover:text-foreground transition-colors ml-1" 
                                  onClick={() => {
                                    navigator.clipboard.writeText(contact.email);
                                    toast({
                                      title: t('carrierManagement.emailCopied'),
                                      description: `${contact.email} ${t('carrierManagement.emailCopiedDescription')}`,
                                    });
                                  }}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span className="truncate" dir="ltr">{formatPhoneNumber(contact.phone)}</span>
                              {contact.phone && contact.phone !== t('carrierManagement.noPhone') && (
                                <Copy 
                                  className="h-3 w-3 text-muted-foreground cursor-pointer hover:text-foreground transition-colors ml-1" 
                                  onClick={() => {
                                    navigator.clipboard.writeText(contact.phone);
                                    toast({
                                      title: t('carrierManagement.phoneCopied'),
                                      description: `${formatPhoneNumber(contact.phone)} ${t('carrierManagement.emailCopiedDescription')}`,
                                    });
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <div className="flex items-center gap-2 justify-center">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm">
                          <p className="text-muted-foreground">{t('carrierManagement.noContactInfo')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                {/* Copy Order Link button hidden for now */}
                {/* {selectedCarrier.status === 'accepted' && (
                  <Button 
                    onClick={() => generateOrderLink(selectedCarrier)}
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Order Link
                  </Button>
                )} */}
                {selectedCarrier.status === 'inactive' && !selectedCarrier.isOutgoing && (
                  <>
                    <Button
                      onClick={() => {
                        handleConnectionAction(selectedCarrier.id, 'accept');
                        setCarrierModalOpen(false);
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {t('carrierManagement.accept')}
                    </Button>
                    <Button
                      onClick={() => {
                        handleConnectionAction(selectedCarrier.id, 'decline');
                        setCarrierModalOpen(false);
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {t('carrierManagement.decline')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('carrierManagement.cancelInvitation')}</DialogTitle>
            <DialogDescription>
              {t('carrierManagement.cancelInvitationConfirm')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t('carrierManagement.keepInvitation')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (invitationToCancel) {
                  handleCancelInvitation(invitationToCancel);
                }
                setCancelDialogOpen(false);
                setInvitationToCancel(null);
              }}
            >
              {t('carrierManagement.yesCancelInvitation')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};